/**
 * Ruta `/justificar/$day?tipo=LATE|EARLY_LEAVE|ABSENCE` (008 · T111; contracts/ui.md §2.3).
 *
 * `JustifyGate` = `useAccessGate({ allowSuspended: false })`: con la empresa suspendida la puerta
 * manda a `/servicio-suspendido` (justificar está prohibido; consultar se hace desde Asistencia).
 * Sin barra de pestañas: es una pantalla de tarea, como `/regularizar/$markId`.
 *
 * `validateSearch` acepta solo `tipo` ∈ `JustificationKind` (`JustificationKindSchema`); lo demás
 * se descarta y la pantalla dice "Este día ya no se puede justificar" en vez de romperse con una
 * URL manipulada. El `$day` tampoco se confía: si no es un día del trabajador que el servidor
 * permita justificar, la pantalla responde igual.
 */
import { createFileRoute } from '@tanstack/react-router';

import { JustificationKindSchema, type JustificationKind } from '@fermin/contracts';

import { GenericErrorScreen } from '../components/ui/generic-error';
import { OfflineState } from '../components/ui/offline-state';
import { Screen } from '../components/ui/screen';
import { PrivacyUpdateScreen } from '../features/access/privacy-update';
import { JustifyLoading, JustifyScreen } from '../features/justification/justify-screen';
import { useAccessGate } from '../features/shell/use-access-gate';

export interface JustificarSearch {
  tipo?: JustificationKind;
}

export function JustifyGate() {
  const { day } = Route.useParams();
  const { tipo } = Route.useSearch();
  const gate = useAccessGate({ allowSuspended: false });

  if (gate.status === 'LOADING') {
    return <JustifyLoading />;
  }
  if (gate.status === 'OFFLINE') {
    return (
      <Screen>
        <OfflineState onRetry={gate.retry} />
      </Screen>
    );
  }
  if (gate.status === 'ERROR') {
    return <GenericErrorScreen onRetry={gate.retry} />;
  }
  if (gate.status === 'PRIVACY_PENDING') {
    return <PrivacyUpdateScreen version={gate.me.privacy_notice.version} />;
  }
  return <JustifyScreen day={day} kind={tipo} />;
}

export const Route = createFileRoute('/justificar/$day')({
  validateSearch: (search: Record<string, unknown>): JustificarSearch => {
    const tipo = JustificationKindSchema.safeParse(search['tipo']);
    return tipo.success ? { tipo: tipo.data } : {};
  },
  component: JustifyGate,
});
